import { MMKV } from 'react-native-mmkv';
import { Platform } from 'react-native';
import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';
import { v4 as uuidv4 } from 'uuid';

import {
  CHECKLIST_SYNC_RECOVERY_FEATURE,
  IS_CHECKLIST_SYNC_RECOVERY_CANARY,
} from '@/config/recoveryCanary';
import type { RecoveryGateDecision, RecoveryIdentity } from '@/types/recovery';
import { getRuntimeClientBuild } from '@/services/runtime/clientBuild';
import { RecoveryGateService } from './RecoveryGateService';

export type RecoveryRuntimeGateStatus =
  | 'disabled'
  | 'checking'
  | 'allowed'
  | 'local_only'
  | 'consent_required'
  | 'recovery_blocked'
  | 'error';

export interface RecoveryRuntimeGateSnapshot {
  status: RecoveryRuntimeGateStatus;
  blocked: boolean;
  identity: RecoveryIdentity | null;
  decision: RecoveryGateDecision | null;
  reason: string | null;
}

const allowsLocalOnlyAccess = (snapshot: RecoveryRuntimeGateSnapshot): boolean => (
  snapshot.status === 'local_only'
  && snapshot.identity !== null
  && snapshot.decision?.blocked === false
  && (snapshot.decision.phase === 'none' || snapshot.decision.phase === 'completed')
);

export const shouldBlockRecoveryUi = (
  snapshot: RecoveryRuntimeGateSnapshot
): boolean => snapshot.blocked && !allowsLocalOnlyAccess(snapshot);

export type RecoverySubscriptionLookupMode = 'blocked' | 'local_only' | 'remote_allowed';

export const decideRecoverySubscriptionLookup = (
  snapshot: RecoveryRuntimeGateSnapshot,
  userId: string | undefined
): RecoverySubscriptionLookupMode => {
  if (!userId || snapshot.identity?.userId !== userId) return 'blocked';
  if (!snapshot.blocked) return 'remote_allowed';
  return snapshot.status === 'local_only' && !shouldBlockRecoveryUi(snapshot)
    ? 'local_only'
    : 'blocked';
};

export const decideRecoveryReconnectAttempt = (input: {
  snapshot: RecoveryRuntimeGateSnapshot;
  isOnline: boolean;
  userId: string | undefined;
  attemptedIdentityKey: string | null;
}): { shouldEvaluate: boolean; attemptedIdentityKey: string | null } => {
  if (!input.isOnline) {
    return { shouldEvaluate: false, attemptedIdentityKey: null };
  }

  const identity = input.snapshot.identity;
  const eligible = input.snapshot.blocked
    && allowsLocalOnlyAccess(input.snapshot)
    && Boolean(input.userId)
    && identity?.userId === input.userId;
  if (!eligible || !identity) {
    return { shouldEvaluate: false, attemptedIdentityKey: input.attemptedIdentityKey };
  }

  const identityKey = `${identity.companyId}:${identity.userId}:${identity.deviceId}`;
  return {
    shouldEvaluate: input.attemptedIdentityKey !== identityKey,
    attemptedIdentityKey: identityKey,
  };
};

interface RecoveryFeatureGateway {
  isEnabled(input: {
    companyId: string;
    userId: string;
    featureKey: string;
    clientBuild: number;
  }): Promise<boolean>;
}

interface RecoveryRuntimeGateDependencies {
  enabled: boolean;
  featureGateway: RecoveryFeatureGateway;
  recoveryGate: Pick<RecoveryGateService, 'hydrateAndEvaluate'>;
}

interface RecoveryFeatureRow {
  enabled?: unknown;
  min_build?: unknown;
  config?: unknown;
}

export const isRecoveryFeatureEnabledForIdentity = (
  row: RecoveryFeatureRow | null,
  userId: string,
  clientBuild: number
): boolean => {
  if (row?.enabled !== true) return false;
  if (
    row.min_build !== null
    && row.min_build !== undefined
    && (typeof row.min_build !== 'number' || clientBuild < row.min_build)
  ) {
    return false;
  }

  const config = row.config;
  if (!config || typeof config !== 'object' || !('user_ids' in config)) {
    return false;
  }
  const userIds = (config as { user_ids?: unknown }).user_ids;
  return Array.isArray(userIds)
    && userIds.every(configuredUserId => typeof configuredUserId === 'string')
    && userIds.includes(userId);
};

const DEVICE_STORAGE_ID = 'app-guincho-recovery-device';
const DEVICE_SEED_KEY = 'recovery_device_seed_v1';
let deviceStorage: MMKV | null = null;

const getDeviceStorage = (): MMKV => {
  if (!deviceStorage) {
    deviceStorage = new MMKV({ id: DEVICE_STORAGE_ID });
  }
  return deviceStorage;
};

export const getStableRecoveryDeviceId = (): string => {
  const storage = getDeviceStorage();
  let seed = storage.getString(DEVICE_SEED_KEY);
  if (!seed) {
    seed = uuidv4();
    storage.set(DEVICE_SEED_KEY, seed);
  }
  return Array.from(nacl.hash(naclUtil.decodeUTF8(seed)))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');
};

const disabledSnapshot = (): RecoveryRuntimeGateSnapshot => ({
  status: 'disabled',
  blocked: false,
  identity: null,
  decision: null,
  reason: null,
});

export const shouldEnableRecoveryRuntimeGate = (
  canaryEnabled: boolean,
  platform: string
): boolean => canaryEnabled && platform !== 'web';

export const getRecoverySubscriptionLookupContext = (input: {
  canaryEnabled: boolean;
  platform: string;
  snapshot: RecoveryRuntimeGateSnapshot;
  userId: string | undefined;
}): { enabled: boolean; mode: RecoverySubscriptionLookupMode } => {
  const enabled = shouldEnableRecoveryRuntimeGate(input.canaryEnabled, input.platform);
  return {
    enabled,
    mode: enabled
      ? decideRecoverySubscriptionLookup(input.snapshot, input.userId)
      : 'remote_allowed',
  };
};

export const canRunRecoveryReconnect = (input: {
  enabled: boolean;
  platform: string;
  snapshot: RecoveryRuntimeGateSnapshot;
  expectedUserId?: string;
  expectedCompanyId?: string | null;
  currentUserId?: string;
  profileId?: string | null;
  profileCompanyId?: string | null;
  companyId?: string | null;
  isOnline: boolean;
}): boolean => {
  if (!input.isOnline || !input.expectedUserId || !input.expectedCompanyId
    || input.currentUserId !== input.expectedUserId
    || input.profileId !== input.expectedUserId
    || input.profileCompanyId !== input.expectedCompanyId
    || input.companyId !== input.expectedCompanyId) return false;
  if (!shouldEnableRecoveryRuntimeGate(input.enabled, input.platform)) return true;
  return !input.snapshot.blocked && input.snapshot.status === 'allowed'
    && input.snapshot.identity?.userId === input.expectedUserId
    && input.snapshot.identity?.companyId === input.expectedCompanyId;
};

const initialSnapshot = (
  enabled: boolean = IS_CHECKLIST_SYNC_RECOVERY_CANARY
): RecoveryRuntimeGateSnapshot => (
  enabled
    ? {
        status: 'checking',
        blocked: true,
        identity: null,
        decision: null,
        reason: 'runtime_gate_not_evaluated',
      }
    : disabledSnapshot()
);

export class RecoveryRuntimeGate {
  private snapshot: RecoveryRuntimeGateSnapshot;
  private revision = 0;
  private readonly listeners = new Set<(snapshot: RecoveryRuntimeGateSnapshot) => void>();

  constructor(private readonly dependencies: RecoveryRuntimeGateDependencies) {
    this.snapshot = dependencies.enabled
      ? {
          status: 'checking',
          blocked: true,
          identity: null,
          decision: null,
          reason: 'runtime_gate_not_evaluated',
        }
      : disabledSnapshot();
  }

  getSnapshot(): RecoveryRuntimeGateSnapshot {
    return this.snapshot;
  }

  subscribe(listener: (snapshot: RecoveryRuntimeGateSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.snapshot);
    return () => this.listeners.delete(listener);
  }

  async evaluate(input: {
    companyId: string;
    userId: string;
    deviceId: string;
    clientBuild: number;
  }): Promise<RecoveryRuntimeGateSnapshot> {
    const revision = ++this.revision;
    if (!this.dependencies.enabled) {
      this.publish(disabledSnapshot());
      return this.snapshot;
    }

    const identity: RecoveryIdentity = {
      companyId: input.companyId.trim(),
      userId: input.userId.trim(),
      deviceId: input.deviceId.trim(),
    };
    const preserveLocalOnly = this.snapshot.status === 'local_only'
      && !shouldBlockRecoveryUi(this.snapshot)
      && this.snapshot.identity?.companyId === identity.companyId
      && this.snapshot.identity.userId === identity.userId
      && this.snapshot.identity.deviceId === identity.deviceId;
    if (!preserveLocalOnly) {
      this.publish({
        status: 'checking',
        blocked: true,
        identity,
        decision: null,
        reason: 'runtime_gate_checking',
      });
    }

    if (!identity.companyId || !identity.userId || !identity.deviceId || input.clientBuild <= 0) {
      return this.failClosed(identity, 'runtime_gate_identity_invalid');
    }

    let enabled: boolean;
    try {
      enabled = await this.dependencies.featureGateway.isEnabled({
        companyId: identity.companyId,
        userId: identity.userId,
        featureKey: CHECKLIST_SYNC_RECOVERY_FEATURE,
        clientBuild: input.clientBuild,
      });
    } catch {
      if (!this.isCurrent(revision)) return this.snapshot;
      return this.evaluateLocalState(identity, revision);
    }
    if (!this.isCurrent(revision)) return this.snapshot;

    try {
      if (!enabled) {
        this.publish({
          status: 'allowed',
          blocked: false,
          identity,
          decision: null,
          reason: 'company_feature_disabled',
        });
        return this.snapshot;
      }

      const decision = await this.dependencies.recoveryGate.hydrateAndEvaluate(identity);
      if (!this.isCurrent(revision)) return this.snapshot;
      if (decision.phase === 'none') {
        this.publish({
          status: 'consent_required',
          blocked: true,
          identity,
          decision,
          reason: 'recovery_consent_required',
        });
      } else if (decision.blocked) {
        this.publish({
          status: 'recovery_blocked',
          blocked: true,
          identity,
          decision,
          reason: decision.reasons[0] ?? 'recovery_in_progress',
        });
      } else {
        this.publish({
          status: 'allowed',
          blocked: false,
          identity,
          decision,
          reason: 'recovery_completed',
        });
      }
      return this.snapshot;
    } catch {
      if (!this.isCurrent(revision)) return this.snapshot;
      return this.failClosed(identity, 'runtime_gate_evaluation_failed');
    }
  }

  reset(): void {
    this.revision += 1;
    this.publish(initialSnapshot(this.dependencies.enabled));
  }

  private failClosed(identity: RecoveryIdentity, reason: string): RecoveryRuntimeGateSnapshot {
    this.publish({
      status: 'error',
      blocked: true,
      identity,
      decision: null,
      reason,
    });
    return this.snapshot;
  }

  private async evaluateLocalState(
    identity: RecoveryIdentity,
    revision: number
  ): Promise<RecoveryRuntimeGateSnapshot> {
    try {
      const decision = await this.dependencies.recoveryGate.hydrateAndEvaluate(identity);
      if (!this.isCurrent(revision)) return this.snapshot;
      if (!decision.blocked && (decision.phase === 'none' || decision.phase === 'completed')) {
        this.publish({
          status: 'local_only',
          blocked: true,
          identity,
          decision,
          reason: decision.phase === 'completed'
            ? 'remote_gate_unavailable_local_recovery_completed'
            : 'remote_gate_unavailable_no_local_recovery',
        });
        return this.snapshot;
      }
    } catch {
      if (!this.isCurrent(revision)) return this.snapshot;
      // Estado ativo, divergente ou ilegivel continua fail-closed.
    }
    return this.failClosed(identity, 'runtime_gate_evaluation_failed');
  }

  private isCurrent(revision: number): boolean {
    return revision === this.revision;
  }

  private publish(snapshot: RecoveryRuntimeGateSnapshot): void {
    this.snapshot = snapshot;
    this.listeners.forEach(listener => listener(snapshot));
  }
}

const supabaseFeatureGateway: RecoveryFeatureGateway = {
  async isEnabled(input) {
    const { supabase } = require('@/services/supabase') as typeof import('@/services/supabase');
    const { data, error } = await supabase
      .from('guincho_company_features')
      .select('enabled,min_build,config')
      .eq('empresa_id', input.companyId)
      .eq('feature_key', input.featureKey)
      .maybeSingle();
    if (error) throw error;
    return isRecoveryFeatureEnabledForIdentity(data, input.userId, input.clientBuild);
  },
};

let defaultRecoveryGateService: RecoveryGateService | null = null;
const lazyRecoveryGateService: Pick<RecoveryGateService, 'hydrateAndEvaluate'> = {
  hydrateAndEvaluate(identity) {
    if (!defaultRecoveryGateService) {
      defaultRecoveryGateService = new RecoveryGateService();
    }
    return defaultRecoveryGateService.hydrateAndEvaluate(identity);
  },
};

export const recoveryRuntimeGate = new RecoveryRuntimeGate({
  // O escrow e a limpeza local controlada existem apenas no runtime nativo.
  // O web consome os snapshots, mas nunca deve exigir recovery por dispositivo.
  enabled: shouldEnableRecoveryRuntimeGate(IS_CHECKLIST_SYNC_RECOVERY_CANARY, Platform.OS),
  featureGateway: supabaseFeatureGateway,
  recoveryGate: lazyRecoveryGateService,
});

export const evaluateRecoveryRuntimeGate = (input: {
  companyId: string;
  userId: string;
}): Promise<RecoveryRuntimeGateSnapshot> => recoveryRuntimeGate.evaluate({
  ...input,
  deviceId: getStableRecoveryDeviceId(),
  clientBuild: getRuntimeClientBuild(),
});

export const isRecoveryRuntimeBlocked = (): boolean =>
  recoveryRuntimeGate.getSnapshot().blocked;
